/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { computed, Injectable, signal, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Option } from '../../option/option';
import { OptionSelectionService } from '../option-selection.service';
import { SelectionEvent } from '../selection-event';
import { MultiSelection } from './multi-selection';

@Injectable()
export class OptionMultiSelectionService extends OptionSelectionService {
  private readonly state = signal<MultiSelection>({values: [], userInput: false});

  readonly selection: Signal<MultiSelection> = this.state.asReadonly();
  readonly selection$: Observable<MultiSelection> = toObservable(this.state);

  getSelection(): MultiSelection {
    return this.state();
  }

  selectAll(event: MultiSelection): void {
    this.state.set(event);
  }

  select(event: SelectionEvent): void {
    const selected = this.state().values || [];

    const newOptions = selected.some(value => super.compareValues(event.value, value))
      ? selected.filter(value => !super.compareValues(event.value, value))
      : [...selected, event.value];

    this.state.set({values: newOptions, userInput: event.userInput});
  }

  isSelected(option: Option): Signal<boolean> {
    return computed(() => this.state().values?.some(value => super.compareValues(option.value, value)) ?? false);
  }

  isAnySelected(options: Option[]): Signal<boolean> {
    return computed(() => {
      const values = this.state().values;
      return options.some(option => values?.some(value => super.compareValues(option.value, value))) ?? false;
    });
  }

  isSelected$(option: Option): Observable<boolean> {
    return this.selection$.pipe(
      map(event => event.values?.some(value => super.compareValues(option.value, value)) ?? false),
    );
  }

  isAnySelected$(options: Option[]): Observable<boolean> {
    return this.selection$.pipe(
      map(event => options.some(option => event.values?.some(value => super.compareValues(option.value, value)))));
  }

  clear(event: { userInput: boolean }): void {
    this.state.set({values: [], userInput: event.userInput});
  }
}
